import { QuestionFormProps } from "@components/AskTheExperts/QuestionForm/QuestionForm";
import { QuestionListProps } from "@components/AskTheExperts/QuestionList/QuestionList";
import { JWMCard } from "@components/JWMCard/JWMCard";
import { MaterialsCard } from "@components/MaterialsCard/MaterialsCard";
import { VideoPlayer } from "@components/VideoPlayer/VideoPlayer";
import { Locator } from "@jmc/core/src/components/Locator/Locator";
import { FlexPosition, ItemPosition } from "@jmc/solid-design-system/src/components/atoms/Grid/Grid";
import { Separator } from "@jmc/solid-design-system/src/components/atoms/Separator/Separator";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Colors as TypographyColors } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { AudioCard } from "@jmc/solid-design-system/src/components/molecules/AudioCard/AudioCard";
import {
    HorizontalAlignment,
    ImagePlacement,
    Padding,
} from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { VideoCard } from "@jmc/solid-design-system/src/components/molecules/VideoCard/VideoCard";
import { Carousel } from "@jmc/solid-design-system/src/components/organisms/Carousel/Carousel";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { CMSATEFormPageProps, CMSATEQuestionPageProps } from "@jwm/types/CMSAskTheExpertsProps";
import { CMSAudioCardProps } from "@jwm/types/CMSAudioCardProps";
import { CMSCardProps } from "@jwm/types/CMSCardProps";
import { CMSCarouselProps } from "@jwm/types/CMSCarouselProps";
import { CMSColor } from "@jwm/types/CMSColor";
import { CMSDecorativeImageProps } from "@jwm/types/CMSDecorativeImage";
import { CMSMapProps } from "@jwm/types/CMSMapProps";
import { CMSMaterialsCardProps } from "@jwm/types/CMSMaterialsCardProps";
import { CMSRecipeGridProps } from "@jwm/types/CMSRecipeGridProps";
import { CMSColumnContentAlignment, CMSColumnHorizontalAlign, CMSColumnVerticalAlign } from "@jwm/types/CMSRowProps";
import { CMSSectionProps } from "@jwm/types/CMSSectionProps";
import { CMSSeparatorProps } from "@jwm/types/CMSSeparatorProps";
import { CMSTypographyProps } from "@jwm/types/CMSTypographyProps";
import { CMSVideoCardProps } from "@jwm/types/CMSVideoCardProps";
import React from "react";

import podcastIcons from "../../../static/svgs/player-logo/jnjBrandingSprite.svg";
import icons from "../../../static/svgs/player-logo/sprite.svg";
import { CMS_CARD_VARIANT_BACKGROUND } from "../../types/CMSCardProps";
import {
    backgroundMap,
    CardColor,
    cardColorMap,
    CarouselColor,
    carouselColorMap,
    concertinaTypeMap,
    gridMap,
    GridSizes,
    rebrandedBackgroundMap,
    rebrandedCardColorMap,
    sectionColorMap,
} from "./CMSMapperDefinitions";
import { mapButtonProps } from "./mapButtonProps";

const mapCardColors = (cmsColor: CMSColor): CardColor => {
    return cardColorMap.get(cmsColor);
};

const mapRebrandedCardColors = (cmsColor: CMSColor): CardColor => {
    return rebrandedCardColorMap.get(cmsColor) || "--transparent";
};

export const mapCarouselColors = (cmsColor: CMSColor): CarouselColor => {
    return carouselColorMap.get(cmsColor);
};

const mapSectionTextColors = (cmsColor: string): string => {
    return sectionColorMap.get(cmsColor);
};

/**
 * transform a CMSCardProps instance into props for a JWMCard
 */
export const mapJWMCardProps = (
    cmsProps: CMSCardProps,
    jnjFullBranded = false,
): React.ComponentProps<typeof JWMCard> => {
    let buttonProps = null;
    let fullBackground;
    let backgroundColor;
    let titleColor;
    let textColor;
    let linkColor;
    let hasOverlay;
    let dropShadow;
    let horizontalAlignment;
    let padding;

    const specialties = cmsProps?.specialty?.map((specialty) => {
        return specialty.specialtyTitle;
    });

    if (cmsProps?.call_to_action?.length) {
        buttonProps = cmsProps.call_to_action?.map(({ button }) => {
            const mappedButtonProps = {
                text: button.text,
                ...mapButtonProps(button),
            };

            if (jnjFullBranded) {
                mappedButtonProps.buttonProps.color = "primary-550";
                mappedButtonProps.buttonProps.variant = "naked";
                mappedButtonProps.buttonProps.flexibleWidth = true;
                mappedButtonProps.buttonProps.justifiedBetween = true;
            }
            return mappedButtonProps;
        });
    }

    const imagePlacement = (placement: string): ImagePlacement => {
        if (jnjFullBranded) {
            return "default";
        }
        switch (placement) {
            default:
            case "Default (40/60 Image left)":
                return "default";
            case "50/50 Image left":
                return "50_img_left";
            case "50/50 Image right":
                return "50_img_right";
        }
    };

    if (jnjFullBranded) {
        fullBackground = false;
        backgroundColor = mapRebrandedCardColors(cmsProps.display_options?.background_color as CMSColor);
        titleColor = "--text-dark";
        textColor = "--text-light";
        linkColor = "--text-link";
        hasOverlay = false;
        dropShadow = false;
        horizontalAlignment = "start";
        padding = "default";
    } else {
        fullBackground = cmsProps?.display_options?.variant_type === CMS_CARD_VARIANT_BACKGROUND;
        backgroundColor =
            cmsProps?.display_options?.background_color !== null
                ? mapCardColors(cmsProps.display_options?.background_color as CMSColor)
                : "--white";

        titleColor =
            cmsProps?.display_options?.title_color !== null
                ? mapCardColors(cmsProps.display_options?.title_color as CMSColor)
                : "--text-primary";

        textColor =
            cmsProps?.display_options?.text_color !== null
                ? mapCardColors(cmsProps.display_options?.text_color as CMSColor)
                : "--text-light";

        linkColor =
            cmsProps?.display_options?.sub_title_reference_and_link_color !== null
                ? mapCardColors(cmsProps.display_options?.sub_title_reference_and_link_color as CMSColor)
                : "--text-link";
        hasOverlay = cmsProps?.display_options?.hasoverlay;
        dropShadow = cmsProps?.display_options?.drop_shadow;
        horizontalAlignment = cmsProps?.display_options?.horizontal_alignment
            ? cmsProps?.display_options?.horizontal_alignment?.toLowerCase()
            : "start";
        padding = cmsProps?.display_options?.padding ? cmsProps?.display_options?.padding?.toLowerCase() : "default";
    }

    return {
        id: cmsProps?.display_title,
        title: cmsProps?.display_title,
        description: cmsProps?.card_body,
        specialty: specialties,
        image: cmsProps?.card_image,
        imageFocus: cmsProps.display_options?.image_focus?.toLowerCase() || "center-center",
        fullBackground: fullBackground,
        horizontalAlignment: horizontalAlignment as HorizontalAlignment,
        padding: padding as Padding,
        imagePlacement: imagePlacement(cmsProps?.display_options?.image_placement),
        dropShadow: dropShadow,
        hasOverlay: hasOverlay,
        backgroundColor: backgroundColor,
        titleColor: titleColor,
        textColor: textColor,
        linkColor: linkColor,
        button: buttonProps,
    };
};

/**
 * transform a CMSVideoCardProps instance into props for a VideoCard component.
 */
export const mapVideoCardProps = (cmsProps: CMSVideoCardProps): React.ComponentProps<typeof VideoCard> => {
    const video = cmsProps?.video[cmsProps.video.length - 1];
    const specialties = cmsProps?.specialty?.map((specialty) => {
        return specialty.specialtyTitle;
    });

    const seoTitletag = video?.seo_settings?.title_tag || video?.title;
    return {
        showTitle: cmsProps?.display_options.show_title,
        showDescription: cmsProps?.display_options?.show_description,
        dropShadow: cmsProps?.display_options?.drop_shadow,
        imageFocus: cmsProps?.display_options?.image_focus?.toLowerCase() || "center-center",
        title: video?.title,
        description: video?.summary,
        specialty: specialties,
        mediasource: video?.media_source,
        seoTitle: seoTitletag,
        alt: video?.poster_image?.description,
    };
};

export const mapVideoProps = (cmsProps: CMSVideoCardProps): React.ComponentProps<typeof VideoPlayer> => {
    const video = cmsProps.video[cmsProps.video.length - 1];
    return {
        videoId: video?.video_id,
        title: video?.title,
        summary: video?.summary,
        thumbnail: video?.poster_image?.url,
        showControls: cmsProps?.display_options?.show_controls,
        showLargePlayButton: cmsProps?.display_options?.show_large_play_button,
        autoplay: cmsProps.display_options?.autoplay,
        mediasource: video?.media_source,
        seoTitle: video?.seo_settings?.title_tag,
        alt: video?.poster_image?.description,
        interactive_video: video?.interactive_video,
        updated_at: video?.updated_at,
    };
};

type CMSMenuItem = {
    menu_item: {
        label: string;
        icon: string;
        link: {
            url: string;
        }[];
        external_link: {
            href: string;
        };
    };
};

type MenuItem = {
    menu_item: {
        icon: string;
        label: string;
        link: { url: string };
    };
};

export const mapMenuItems = (cmsMenuItems: [CMSMenuItem]): MenuItem[] => {
    return cmsMenuItems.map((cmsItem: CMSMenuItem): MenuItem => {
        return {
            menu_item: {
                icon: cmsItem.menu_item.icon,
                label: cmsItem.menu_item.label,
                link: {
                    url: cmsItem.menu_item.link.length
                        ? cmsItem.menu_item.link[0].url
                        : cmsItem.menu_item.external_link.href,
                },
            },
        };
    });
};

/**
 * transform a CMSTypographyProps instance into props for a Typography component
 */
export const mapTypographyProps = (cmsProps: CMSTypographyProps): React.ComponentProps<typeof Typography> => {
    return {
        text: cmsProps.text,
        // TODO: (AHAY-3441) map with better color/variant names, this approach can be reused for other components like buttons, etc
        color: cmsProps.color as TypographyColors,
        variant: cmsProps.variant,
        children: null,
    };
};

export const mapColumnContentAlignment = (cmsColumnContentAlignment: CMSColumnContentAlignment): ItemPosition => {
    switch (cmsColumnContentAlignment) {
        default:
        case "Spread":
            return "stretch";
        case "Top":
            return "flex-start";
        case "Bottom":
            return "flex-end";
        case "Center":
            return "center";
    }
};

export const mapColumnHorizontalAlign = (cmsColumnHorizontalAlign: CMSColumnHorizontalAlign): FlexPosition => {
    switch (cmsColumnHorizontalAlign) {
        default:
        case "Center":
            return "center";
        case "Start":
            return "flex-start";
        case "End":
            return "flex-end";
    }
};

export const mapColumnVerticalAlign = (cmsColumnVerticalAlign: CMSColumnVerticalAlign): FlexPosition => {
    switch (cmsColumnVerticalAlign) {
        default:
        case "Middle":
            return "center";
        case "Top":
            return "flex-start";

        case "Bottom":
            return "flex-end";
    }
};

export const mapRowLayout = (cmsRowLayout: string): GridSizes[] => {
    if (gridMap.get(cmsRowLayout)) {
        return gridMap.get(cmsRowLayout);
    }

    const from = cmsRowLayout.indexOf("(") + 1;
    const length = cmsRowLayout.indexOf(")") - cmsRowLayout.indexOf("(") - 1;
    return cmsRowLayout
        .substr(from, length)
        .split("-")
        .map((number: string) => ({ xs: 12, sm: 6, md: parseInt(number) }));
};

interface mappedSectionProps {
    backgroundColor: string;
    title: string;
    showTitle: boolean;
    fullWidth: boolean;
    textColor: string;
    titleColor: string;
    refColor: string;
    visitedLinkColor: string;
    reverseTextDirection: boolean;
    noVerticalPadding: boolean;
    decorative_image: CMSDecorativeImageProps[];
}

export const mapSection = (cmsProps: CMSSectionProps): mappedSectionProps => {
    const { jnjFullBranded } = useJnjBranding();
    return {
        backgroundColor: jnjFullBranded
            ? rebrandedBackgroundMap.get(cmsProps?.background_color)
            : backgroundMap.get(cmsProps?.background_color),
        title: cmsProps?.section_title,
        showTitle: cmsProps?.show_title,
        fullWidth: cmsProps?.full_width,
        textColor: jnjFullBranded
            ? "text-light"
            : cmsProps?.text_color && cmsProps?.text_color?.toLowerCase() !== "default"
              ? cmsProps?.text_color?.toLowerCase()
              : "text-dark",
        titleColor:
            cmsProps?.title_color && cmsProps?.title_color?.toLowerCase() !== "default"
                ? cmsProps?.title_color?.toLowerCase()
                : "primary",
        refColor:
            cmsProps?.sub_title_reference_link_color &&
            cmsProps?.sub_title_reference_link_color?.toLowerCase() !== "default"
                ? cmsProps?.sub_title_reference_link_color?.toLowerCase()
                : "primary",
        visitedLinkColor:
            cmsProps?.sub_title_reference_link_color &&
            cmsProps?.sub_title_reference_link_color?.toLowerCase() !== "default"
                ? cmsProps?.sub_title_reference_link_color?.toLowerCase()
                : "primary-400",
        reverseTextDirection: cmsProps?.reverse_text_direction || false,
        noVerticalPadding: cmsProps?.no_vertical_padding,
        decorative_image: cmsProps?.decorative_image,
    };
};

export const mapCarouselProps = (cmsProps: CMSCarouselProps): React.ComponentProps<typeof Carousel> => {
    const buttonPropsCarousel = cmsProps.slides?.map((slide) => mapButtonProps(slide.button));
    return {
        footer: buttonPropsCarousel,
        interval: { auto: cmsProps.interval?.automatic, speed: cmsProps?.interval?.speed * 1000 },
    };
};

const mapStreamingOptions = (
    streamingLinks: {
        streaming_links_section_title?: string;
        google?: string;
        spotify?: string;
        apple?: string;
        deezer?: string;
        castbox?: string;
    },
    jnjFullBranded?: boolean,
): {
    streamingTitle: string;
    streamingLinks: { serviceName: string; logoDefault: string; link: string }[];
} => {
    const options: {
        serviceName: string;
        logoDefault: string;
        link: string;
    }[] = [];

    if (streamingLinks?.google && !jnjFullBranded) {
        const googleLink = /^(?:f|ht)tps?:\/\//.test(streamingLinks.google)
            ? streamingLinks.google
            : "https://" + streamingLinks.google;

        options.push({
            serviceName: "Google",
            logoDefault: `${icons}#logo_google`,
            link: googleLink,
        });
    }

    if (streamingLinks?.spotify) {
        const spotifyLink = /^(?:f|ht)tps?:\/\//.test(streamingLinks.spotify)
            ? streamingLinks.spotify
            : "https://" + streamingLinks.spotify;

        options.push({
            serviceName: "Spotify",
            logoDefault: `${jnjFullBranded ? podcastIcons : icons}#logo_spotify`,
            link: spotifyLink,
        });
    }

    if (streamingLinks?.apple) {
        const appleLink = /^(?:f|ht)tps?:\/\//.test(streamingLinks.apple)
            ? streamingLinks.apple
            : "https://" + streamingLinks.apple;

        options.push({
            serviceName: "Apple",
            logoDefault: `${jnjFullBranded ? podcastIcons : icons}#logo_apple`,
            link: appleLink,
        });
    }

    if (streamingLinks?.deezer) {
        const deezerLink = /^(?:f|ht)tps?:\/\//.test(streamingLinks.deezer)
            ? streamingLinks.deezer
            : "https://" + streamingLinks.deezer;

        options.push({
            serviceName: "Deezer",
            logoDefault: `${jnjFullBranded ? podcastIcons : icons}#logo_deezer`,
            link: deezerLink,
        });
    }

    if (streamingLinks?.castbox) {
        const castboxLink = /^(?:f|ht)tps?:\/\//.test(streamingLinks.castbox)
            ? streamingLinks.castbox
            : "https://" + streamingLinks.castbox;

        options.push({
            serviceName: "Castbox",
            logoDefault: `${jnjFullBranded ? podcastIcons : icons}#logo_castbox`,
            link: castboxLink,
        });
    }
    return {
        streamingTitle: streamingLinks?.streaming_links_section_title,
        streamingLinks: options,
    };
};

/**
 * transform a CMSAudioCardProps instance into props for an AudioCard component.
 */
export const mapAudioCardProps = (cmsProps: CMSAudioCardProps): React.ComponentProps<typeof AudioCard> => {
    const audio = cmsProps?.audio[cmsProps.audio?.length - 1];
    const specialties = cmsProps?.specialty?.map((specialty) => {
        return specialty?.specialtyTitle;
    });

    return {
        id: cmsProps.id,
        showTitle: cmsProps?.display_options?.show_title,
        showDescription: cmsProps?.display_options?.show_description,
        showStreamingOptions: cmsProps?.display_options?.show_streaming_options,
        specialty: specialties,
        dropShadow: cmsProps?.display_options?.drop_shadow,
        fileUrl: audio?.audio_file?.url,
        image: cmsProps?.audio_card_image,
        title: audio?.display_title,
        imageFocus: cmsProps.display_options?.image_focus?.toLowerCase() || "center-center",
        brightcove_id: audio?.brightcove_id,
    };
};

/**
 * transform a CMSMaterialsCardProps instance into props for a MaterialsCard component
 */
export const mapMaterialsCardProps = (cmsProps: CMSMaterialsCardProps): React.ComponentProps<typeof MaterialsCard> => {
    const materials = cmsProps.materials?.[0];
    const specialties = cmsProps?.specialty?.map((specialty) => {
        return specialty.specialtyTitle;
    });

    return {
        id: cmsProps.id,
        showTitle: cmsProps.show_title,
        showDescription: cmsProps.show_description,
        specialty: specialties,
        fileName: materials?.materials_download?.filename,
        fileUrl: materials?.materials_download?.url,
        fileSize: materials?.materials_download?.file_size,
        image: cmsProps.materials_card_image,
        title: materials?.display_title,
        description: materials?.materials_description,
        imageFocus: cmsProps?.image_focus?.toLowerCase() || "center-center",
    };
};

/**
 * transform a CMSSeparatorProps instance into props for a Separator component
 */
export const mapSeparatorProps = (cmsProps: CMSSeparatorProps): React.ComponentProps<typeof Separator> => {
    return {
        color: backgroundMap.get(cmsProps.color),
    };
};

/**
 * get the type prop for a concertina, given the accordion type from the cms
 */
export const mapConcertinaType = (
    cmsConcertinaType: "None" | "First Open" | "Only One",
): "none" | "firstOpen" | "onlyOne" => {
    return concertinaTypeMap.get(cmsConcertinaType);
};

/**
 * transform a CMSMapProps instance into props for an Locator component
 */
export const mapLocatorProps = (cmsProps: CMSMapProps): React.ComponentProps<typeof Locator> => {
    const myTag = cmsProps?.title.replace(/ /g, "") + "-" + cmsProps?.publish_details?.locale;
    return {
        tag: myTag,
        distanceUnit: cmsProps?.distance_unit,
    };
};

/**
 * transform a CMSRecipeGridProps instance into props for RecipeGrid component
 */
export const mapRecipeGridProps = (
    recipe_grid: CMSRecipeGridProps,
): {
    enabledFilters: { diseases: boolean; courses: boolean; dietaryWishes: boolean; levelsOfComplexity: boolean };
    specialty: { id: string; title: string }[];
} => {
    const specialties = recipe_grid.specialty.map((spec: { id: string; title: string; uid: string }) => {
        return {
            id: spec.uid,
            title: spec.title,
        };
    });

    return {
        enabledFilters: {
            diseases: recipe_grid?.enable_filters?.diseases,
            courses: recipe_grid?.enable_filters?.course,
            dietaryWishes: recipe_grid?.enable_filters?.dietary_wishes,
            levelsOfComplexity: recipe_grid?.enable_filters?.level_of_complexity,
        },
        specialty: specialties,
    };
};

export const mapATEForm = (props: CMSATEFormPageProps): QuestionFormProps => {
    return {
        title: props?.title,
        returnPage: {
            url: props?.return_page[0]?.url,
            siteReference: { urlPrefix: props?.return_page[0]?.site_reference[0]?.url_prefix },
        },
        siteSpecialty: {
            title: props?.site_reference[0]?.related_to[0]?.title,
            type: props?.site_reference[0]?.related_to[0]?.type,
        },
    };
};

export const mapATEQuestionList = (props: CMSATEQuestionPageProps): QuestionListProps => {
    return {
        title: props?.title,
        formPage: {
            url: props?.form_page[0]?.url,
            siteReference: { urlPrefix: props?.form_page[0]?.site_reference[0]?.url_prefix },
        },
        siteSpecialty: {
            title: props?.site_reference[0]?.related_to[0]?.title,
            type: props?.site_reference[0]?.related_to[0]?.type,
        },
    };
};

export default {
    mapMenuItems,
    mapTypographyProps,
    mapColumnContentAlignment,
    mapColumnHorizontalAlign,
    mapColumnVerticalAlign,
    mapRowLayout,
    mapSection,
    mapJWMCardProps,
    mapAudioCardProps,
    mapVideoCardProps,
    mapVideoProps,
    mapMaterialsCardProps,
    mapSectionTextColors,
    mapSeparatorProps,
    mapConcertinaType,
    mapLocatorProps,
    mapCarouselProps,
    mapCarouselColors,
    mapStreamingOptions,
    mapRecipeGridProps,
    mapATEForm,
    mapATEQuestionList,
    rebrandedBackgroundMap,
};
